import { useState } from 'react';
import { Wifi } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useDeviceMutation } from '@/hooks/useDeviceMutation';
import { useToast } from '@/hooks/useToast';
import { SectionHeader, Field } from './SectionHeader';
import type { DeviceStatus } from '@/lib/types';

interface Props { device: DeviceStatus; disabled: boolean }

export function WifiSection({ device, disabled }: Props) {
  const [ssid, setSsid] = useState('');
  const [pass, setPass] = useState('');
  const toast = useToast();

  const mut = useDeviceMutation<{ ssid: string; pass: string }>(
    device.client_id, 'wifi', { successToast: 'Wi-Fi credentials saved — device will use them on next boot' },
  );

  const submit = () => {
    if (!ssid.trim() || !pass) { toast.warn('SSID and password required'); return; }
    mut.mutate({ ssid: ssid.trim(), pass });
  };

  const currentSsid = device.wifi?.ssid;

  return (
    <div className="space-y-5">
      <SectionHeader title="Wi-Fi credentials" description="Saves credentials. The device picks them up on the next reboot." />

      {currentSsid && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm">
          <Wifi className="w-4 h-4 text-slate-500" />
          <span className="text-slate-600">Currently connected to</span>
          <span className="font-bold text-ink">{currentSsid}</span>
        </div>
      )}

      <Field label="SSID" htmlFor="wifi-ssid">
        <Input id="wifi-ssid" value={ssid} onChange={(e) => setSsid(e.target.value)}
          disabled={disabled} placeholder="WiFi network name" />
      </Field>
      <Field label="Password" htmlFor="wifi-pass">
        <Input id="wifi-pass" type="password" value={pass} onChange={(e) => setPass(e.target.value)}
          disabled={disabled} placeholder="WiFi password" />
      </Field>
      <Button onClick={submit} loading={mut.isPending} disabled={disabled || mut.isPending}>
        Save Wi-Fi
      </Button>
    </div>
  );
}
